<template>
  <div
    v-if="customIsVisible && sliderItemsLongTexts.length > 0"
    :class="[backgroundColor, bottomMargin, verticalPadding, background]"
    class="content-slider-long-texts"
  >
    <div :data-component-id="componentId" class="mx-auto w-full max-w-screen-2xl px-5 md:px-12">
      <div class="flex flex-col items-center justify-between">
        <div v-if="title" class="flex flex-row justify-start w-full mb-8">
          <VcTypography :tag="heading" :anchor-id="title">
            {{ title }}
          </VcTypography>
        </div>
        <Swiper
          :key="swiperKey"
          :modules="[Navigation]"
          :slides-per-view="itemsPerRow"
          :spaceBetween="sliderItemsPerRowOnDesktop === '3' ? 120 : 40"
          :navigation="contentSwiperNavigationOptions"
          @swiper="onSwiper"
          @slideChange="updateProgressBar()"
          @resize="onSwiperResize"
          ref="contenSwiperRef"
          class="mx-0 w-full"
          data-te-lightbox-init
        >
          <SwiperSlide v-for="(sliderItem, index) in sliderItemsLongTexts" :key="index">
            <InnoContentSliderItemLongTexts :slider-item="sliderItem" />
          </SwiperSlide>
        </Swiper>
      </div>

      <!-- flex h-full border border-[--color-body-bg-alt-1] -->

      <div v-if="sliderItemsLongTexts.length > itemsPerRow" class="flex items-center justify-between px-2.5 mt-5">
        <VcNavButton class="items_prev" :label="$t('common.buttons.previous')" size="xs" direction="left" />

        <div
          id="progressbar"
          class="relative h-1 w-full bg-[--color-categorytile-progressbar] rounded overflow-hidden mx-5"
        >
          <div
            ref="fill"
            class="progress-fill absolute h-full bg-[--color-primary-500] transition-all duration-300"
            style="width: 0%"
          ></div>
        </div>

        <VcNavButton class="items_next" :label="$t('common.buttons.next')" size="xs" direction="right" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref } from "vue";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { Swiper, SwiperSlide } from "swiper/vue";
import InnoContentSliderItemLongTexts from "./inno-content-slider-item-long-texts.vue";
import type SwiperCore from "swiper";
import { Navigation } from "swiper/modules";
import type { IContentSliderLongTextsType } from "@/core/types/innovadis/inno-content-slider-item-long-texts";

import "swiper/css";
import "swiper/css/grid";

interface IProps {
  heading?: string;
  title?: string;
  sliderItemsLongTexts: Array<IContentSliderLongTextsType>;
  sliderItemsPerRowOnDesktop?: string;

  id: string;
  background?: string;
  model: any;
  settings: any;

  // Innovadis custom: Use custom visibility setting
  customIsVisible: boolean;

  // Innovadis custom: Use custom layout settings
  backgroundColor: string;
  bottomMargin: string;
  verticalPadding: string;
}

const props = withDefaults(defineProps<IProps>(), {
  heading: "h2",
  sliderItemsPerRowOnDesktop: "4",
});

const componentId = computed(() => {
  return getCurrentInstance()?.uid;
});

const fill = ref<HTMLElement | null>(null);

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("md");
const isTablet = breakpoints.smaller("lg");

// how many items are shown per row depends on the viewport
const itemsPerRow = computed<number>(() =>
  isMobile.value ? 1 : isTablet.value ? 2 : parseInt(props.sliderItemsPerRowOnDesktop),
);

const contenSwiperRef = ref<SwiperCore | null>(null);

// Reinitialize slider based on itemsPerView (for switch between viewports)
const swiperKey = computed(() => `content-swiper-long-texts-${componentId.value}-${itemsPerRow.value}`);

const contentSwiperNavigationOptions = computed(() => {
  return {
    prevEl: `[data-component-id="${componentId.value}"] .items_prev`,
    nextEl: `[data-component-id="${componentId.value}"] .items_next`,
  };
});

function updateProgressBar() {
  const swiper = contenSwiperRef.value;
  const fillElement = fill.value;

  if (!swiper || !fillElement) return;

  const progressPercent = Math.min(100, Math.round(swiper.progress * 100));
  fillElement.style.width = `${progressPercent}%`;
}

function onSwiper(swiper: SwiperCore) {
  contenSwiperRef.value = swiper;
}

function onSwiperResize() {
  const fillElement = fill.value;
  if (!fillElement) return;

  // Empty progressbar on resize
  fillElement.style.width = `0%`;
}
</script>
